import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import { axFetch } from '../../lib/utils';

interface AuthModalProps {
    showModal: boolean;
    onClose: () => void;
    initialMode?: 'signin' | 'signup';
}

export const AuthModal: React.FC<AuthModalProps> = ({ showModal, onClose, initialMode = 'signin' }) => {
    const { currentTheme } = useTheme();
    const { login } = useAuth();
    const navigate = useNavigate();

    // Mode: 'signin' or 'signup'
    const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);

    // Form state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // UI state
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState('');

    // Reset form when modal closes or mode changes
    React.useEffect(() => {
        if (!showModal) {
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setAuthError('');
            // Don't reset mode here, or it will override initialMode on next open if we rely on prop changes
            // Instead, we should update mode when showModal becomes true or initialMode changes
        } else {
            setMode(initialMode);
        }
    }, [showModal, initialMode]);

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validation
        if (!email.trim()) {
            setAuthError('Email is required');
            return;
        }

        if (!password.trim()) {
            setAuthError('Password is required');
            return;
        }

        if (mode === 'signup') {
            if (!confirmPassword.trim()) {
                setAuthError('Please confirm your password');
                return;
            }

            if (password !== confirmPassword) {
                setAuthError('Passwords do not match');
                return;
            }
        }

        setIsAuthenticating(true);
        setAuthError('');

        try {
            const endpoint = mode === 'signin' ? 'sign-in' : 'sign-up';
            const response = await axFetch('POST', endpoint, { email, password });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.token) {
                    // Use the auth context to handle login
                    login(data.token);
                    onClose();

                    // Navigate to dashboard after successful authentication
                    navigate('/dashboard');
                } else {
                    setAuthError(data.error || 'Authentication failed');
                }
            } else {
                const errorData = await response.json();
                setAuthError(errorData.error || 'Authentication failed');
            }
        } catch (error) {
            console.error('Authentication error:', error);
            setAuthError('Authentication failed. Please try again.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const handleClose = () => {
        setAuthError('');
        onClose();
    };

    if (!showModal) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className={`${currentTheme.colors.background.card} ${currentTheme.colors.border.primary} ${currentTheme.borders.thin} ${currentTheme.radius.lg} p-6 w-full max-w-md mx-4`}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-semibold ${currentTheme.colors.text.primary}`}>
                        {mode === 'signin' ? 'Sign In' : 'Sign Up'}
                    </h3>
                    <button
                        onClick={handleClose}
                        className={`${currentTheme.colors.text.tertiary} hover:${currentTheme.colors.text.primary} transition-colors duration-200`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className={`block text-sm font-medium ${currentTheme.colors.text.secondary} mb-2`}>
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Enter your email"
                                required
                                className={`w-full px-3 py-2 ${currentTheme.colors.background.tertiary} ${currentTheme.colors.border.secondary} ${currentTheme.borders.thin} ${currentTheme.radius.md} ${currentTheme.colors.text.primary} ${currentTheme.colors.text.placeholder} focus:outline-none focus:ring-2 focus:ring-green-500`}
                                disabled={isAuthenticating}
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className={`block text-sm font-medium ${currentTheme.colors.text.secondary} mb-2`}>
                                Password <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Enter your password"
                                required
                                className={`w-full px-3 py-2 ${currentTheme.colors.background.tertiary} ${currentTheme.colors.border.secondary} ${currentTheme.borders.thin} ${currentTheme.radius.md} ${currentTheme.colors.text.primary} ${currentTheme.colors.text.placeholder} focus:outline-none focus:ring-2 focus:ring-green-500`}
                                disabled={isAuthenticating}
                            />
                        </div>

                        {mode === 'signup' && (
                            <div>
                                <label className={`block text-sm font-medium ${currentTheme.colors.text.secondary} mb-2`}>
                                    Confirm Password <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Confirm your password"
                                    required
                                    className={`w-full px-3 py-2 ${currentTheme.colors.background.tertiary} ${currentTheme.colors.border.secondary} ${currentTheme.borders.thin} ${currentTheme.radius.md} ${currentTheme.colors.text.primary} ${currentTheme.colors.text.placeholder} focus:outline-none focus:ring-2 focus:ring-green-500`}
                                    disabled={isAuthenticating}
                                />
                            </div>
                        )}

                        {authError && (
                            <div className={`${currentTheme.colors.background.error} ${currentTheme.colors.border.error} ${currentTheme.borders.thin} ${currentTheme.radius.md} p-3`}>
                                <p className={`text-sm ${currentTheme.colors.text.error}`}>{authError}</p>
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <Button
                                type="submit"
                                disabled={isAuthenticating}
                                className={`flex-1 ${currentTheme.colors.brand.primary} ${currentTheme.colors.brand.primaryHover} ${currentTheme.colors.text.inverse} ${currentTheme.colors.interactive.disabled}`}
                            >
                                {isAuthenticating ? (
                                    <>
                                        <div className={`w-4 h-4 ${currentTheme.borders.thick} ${currentTheme.colors.border.tertiary} border-t-white ${currentTheme.radius.full} animate-spin mr-2`}></div>
                                        {mode === 'signin' ? 'Signing In...' : 'Signing Up...'}
                                    </>
                                ) : (
                                    mode === 'signin' ? 'Sign In' : 'Sign Up'
                                )}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleClose}
                                variant="outline"
                                disabled={isAuthenticating}
                                className={`${currentTheme.colors.border.secondary} ${currentTheme.colors.text.secondary} ${currentTheme.colors.interactive.hover} ${currentTheme.colors.interactive.disabled}`}
                            >
                                Cancel
                            </Button>
                        </div>

                        <div className="pt-2 border-t border-gray-700">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode(mode === 'signin' ? 'signup' : 'signin');
                                    setAuthError('');
                                    setConfirmPassword('');
                                }}
                                className={`text-sm ${currentTheme.colors.text.tertiary} hover:${currentTheme.colors.text.primary} transition-colors duration-200`}
                            >
                                {mode === 'signin' ? (
                                    <>Don't have an account? <span className="text-green-500 font-medium">Sign Up</span></>
                                ) : (
                                    <>Already have an account? <span className="text-green-500 font-medium">Sign In</span></>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};